import {
  BackupStatus,
  ResourceType,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import { useRouter } from 'next/navigation';
import React, { forwardRef, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { BigQueryActionRequiredDialog } from '@/components/dialogs/bigQueryActionRequiredDialog';
import { SqlVmActionRequiredDialog } from '@/components/dialogs/sqlvmActionRequiredDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import type { OptionType } from '@/components/option-menu/option-menu';
import { AppsEditor } from '@/components/panels/instance/tabs/classificationPanels/appsEditor';
import { DataClassEditor } from '@/components/panels/instance/tabs/classificationPanels/dataClassEditor';
import { EnvironmentEditor } from '@/components/panels/instance/tabs/classificationPanels/environmentEditor';
import { RestartApplicationDetection } from '@/components/panels/instance/tabs/classificationPanels/restartApplicationDetection';
import { RestartAutomaticDataClassClassification } from '@/components/panels/instance/tabs/classificationPanels/restartAutomaticDataClassification';
import { RestartAutomaticEnvironmentDetection } from '@/components/panels/instance/tabs/classificationPanels/restartAutomaticEnvironmentDetection';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useRoles } from '@/contexts/useRoles';

import { CancelBackupExclusionDialog } from './cancelBackupExclusionDialog';
import { ExcludeFromBackupDialog } from './excludeFromBackupDialog';
import { InventoryBulkPolicyAssignmentDialog } from './inventoryBulkPolicyAssignmentDialog';
import { hasMixedNativeBackupSelection } from './inventoryResourceUtils';
import { TakeSnapshotDialog } from './takeSnapshotDialog';

const richIcon = (icon: string) => (
  <i className={`${icon} w-[20px] h-[20px] mr-[12px]`} />
);

export function InventoryMultiInstanceMenu(props: {
  entities: InventoryResource[];
  triggerInventoryRefresh?: () => void;
}) {
  const { entities, triggerInventoryRefresh } = props;
  const [excludeFromBackupDialogOpen, setExcludeFromBackupDialogOpen] =
    useState(false);
  const [
    cancelBackupExclusionsDialogOpen,
    setCancelBackupExclusionsDialogOpen,
  ] = useState(false);
  const [completeConfigurationDialogOpen, setCompleteConfigurationDialogOpen] =
    useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [takeSnapshotDialogOpen, setTakeSnapshotDialogOpen] = useState(false);
  const [dataClassesDiagOpen, setDataClassesDiagOpen] = useState(false);
  const [envDiagOpen, setEnvDiagOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [resumeAppsDialogOpen, setResumeAppsDialogOpen] = useState(false);
  const [resumeDataClassesDialogOpen, setResumeDataClassesDialogOpen] =
    useState(false);
  const [resumeEnvDialogOpen, setResumeEnvDialogOpen] = useState(false);

  const { isAuthorizedResource } = useRoles();
  const { isDemo, isDev } = useEnvironment();
  const router = useRouter();

  const dlsgCount = entities.filter(
    (entity) => entity.backupStatus === BackupStatus.Dlsg
  ).length;

  // Check if selection contains both native (FSx) and non-native resources
  const isMixedSelection = hasMixedNativeBackupSelection(entities);

  const onEntityChange = async () => {
    triggerInventoryRefresh?.();
  };

  const actions: OptionType[] = [
    {
      id: 'backup-management',
      text: 'Backup Management',
      groupHeader: true,
    },
    {
      id: 'assign-to-backup-policy',
      text: 'Assign to backup policy',
      icon: richIcon('material-symbols-bookmark-add-outline'),
      menuItemProps: {
        disabled: entities.some(
          (entity) => !isAuthorizedResource('update:policies', entity)
        ),
        onClick: () => setAssignDialogOpen(true),
      },
    },
    {
      id: 'take-snapshot',
      text: 'Take snapshot',
      icon: richIcon('material-symbols-backup-outline-rounded'),
      menuItemProps: {
        disabled:
          entities.some(
            (entity) => !isAuthorizedResource('create:jobs', entity)
          ) ||
          // temporary disable option for cross cloud resources
          entities.some(
            (resource) => resource.cloudProvider !== entities[0].cloudProvider
          ) ||
          (dlsgCount > 0 && dlsgCount < entities.length) ||
          // Disable when selection contains both native (FSx) and non-native resources
          isMixedSelection ||
          entities.every(
            (entity) =>
              entity.backupStatus === BackupStatus.Terminated ||
              entity.backupStatus === BackupStatus.Disconnected ||
              entity.backupStatus === BackupStatus.Unsupported ||
              entity.backupStatus === BackupStatus.LimitExceeded
          ) ||
          entities.some(
            (entity) =>
              entity.resourceType === ResourceType.GcpBigQuery &&
              entity.backupStatus === BackupStatus.ActionRequired
          ),
        onClick: () => setTakeSnapshotDialogOpen(true),
      },
    },
    {
      id: 'exclude-from-backup',
      text: 'Exclude from backup',
      icon: richIcon('material-symbols-hide-source-outline-rounded'),
      menuItemProps: {
        disabled:
          entities.some(
            (entity) => !isAuthorizedResource('update:exclude_resource', entity)
          ) ||
          entities.every(
            (entity) => entity.backupStatus === BackupStatus.ExcludedFromBackup
          ),
        onClick: () => setExcludeFromBackupDialogOpen(true),
      },
    },
    {
      id: 'cancel-backup-exclusions',
      text: 'Cancel backup exclusion',
      icon: richIcon('material-symbols-add-circle-outline'),
      menuItemProps: {
        disabled: entities.some(
          (entity) =>
            !isAuthorizedResource('update:exclude_resource', entity) ||
            entity.backupStatus !== BackupStatus.ExcludedFromBackup
        ),
        onClick: () => setCancelBackupExclusionsDialogOpen(true),
      },
    },
    {
      id: 'complete-configuration',
      text: 'Complete configuration',
      icon: richIcon('material-symbols-settings-ethernet'),
      menuItemProps: {
        disabled: entities.some(
          (entity) =>
            !isAuthorizedResource('update:validate_configuration', entity) ||
            entity.backupStatus !== BackupStatus.ActionRequired ||
            (entity.resourceType !== ResourceType.AzureSqlVirtualMachine &&
              entity.resourceType !== ResourceType.GcpBigQuery)
        ),
        onClick: () => setCompleteConfigurationDialogOpen(true),
      },
    },
    {
      id: 'auto-classifications',
      text: 'AUTO-CLASSIFICATIONS',
      groupHeader: true,
    },
    {
      id: 'pause-and-override',
      text: 'Pause and override',
      icon: richIcon('mdi-stop-circle-outline'),
      subMenu: [
        {
          id: 'pause-and-override-data-classes',
          text: 'Data classes',
          menuItemProps: {
            disabled: entities.some(
              (entity) =>
                !isAuthorizedResource('update:data_classification', entity)
            ),
            onClick: () => setDataClassesDiagOpen(true),
          },
        },
        {
          id: 'pause-and-override-environment',
          text: 'Environment',
          menuItemProps: {
            disabled: entities.some(
              (entity) =>
                !isAuthorizedResource(
                  'update:environment_classification',
                  entity
                )
            ),
            onClick: () => setEnvDiagOpen(true),
          },
        },
        {
          id: 'pause-and-override-apps',
          text: 'Applications',
          menuItemProps: {
            disabled: entities.some(
              (entity) =>
                !isAuthorizedResource('update:apps_classification', entity)
            ),
            onClick: () => setAppDialogOpen(true),
          },
        },
      ],
    },
    {
      id: 'resume',
      text: 'Resume',
      icon: richIcon('material-symbols-motion-play-outline-rounded'),
      subMenu: [
        {
          id: 'resume-data-classes',
          text: 'Data classes',
          menuItemProps: {
            disabled: entities.some(
              (entity) =>
                !isAuthorizedResource('update:data_classification', entity)
            ),
            onClick: () => setResumeDataClassesDialogOpen(true),
          },
        },
        {
          id: 'resume-environment',
          text: 'Environment',
          menuItemProps: {
            disabled: entities.some(
              (entity) =>
                !isAuthorizedResource(
                  'update:environment_classification',
                  entity
                )
            ),
            onClick: () => setResumeEnvDialogOpen(true),
          },
        },
        {
          id: 'resume-apps',
          text: 'Applications',
          menuItemProps: {
            disabled: entities.some(
              (entity) =>
                !isAuthorizedResource('update:apps_classification', entity)
            ),
            onClick: () => setResumeAppsDialogOpen(true),
          },
        },
      ],
    },
    ...(isDemo || isDev
      ? [
          {
            id: 'exploration',
            text: 'EXPLORATION',
            groupHeader: true,
          },
          {
            id: 'explore-databases',
            text: 'Explore databases',
            icon: richIcon('material-symbols-frame-inspect-rounded'),
            menuItemProps: {
              disabled: !entities.every(
                (resource) =>
                  resource.resourceType === ResourceType.AwsRds ||
                  resource.resourceType === ResourceType.AtlasMongodbCluster ||
                  resource.resourceType === ResourceType.AzurePostgresql ||
                  resource.resourceType === ResourceType.AzureMysql ||
                  resource.resourceType === ResourceType.AzureSqlDatabase ||
                  resource.resourceType === ResourceType.AwsDynamoDb ||
                  resource.resourceType === ResourceType.GcpCloudSqlInstance ||
                  resource.resourceType ===
                    ResourceType.AzureSqlManagedInstance ||
                  resource.resourceType === ResourceType.GcpCloudFirestore ||
                  ((resource.resourceType === ResourceType.AwsEc2 ||
                    resource.resourceType ===
                      ResourceType.AzureVirtualMachine ||
                    resource.resourceType ===
                      ResourceType.GcpComputeEngineInstance ||
                    resource.resourceType ===
                      ResourceType.AzureSqlVirtualMachine) &&
                    !!resource.classifications?.appsDetails?.apps?.find(
                      (app) => app.paths?.length
                    ))
              ),
              onClick: () =>
                router.push(
                  `/databases/${entities.map((entity) => entity.id).join(',')}`
                ),
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <OptionMenu
        options={actions}
        Component={forwardRef<HTMLButtonElement>((props, ref) => (
          <ButtonWithIcon
            text='Actions'
            icon='material-symbols-more-horiz'
            variant='outlined'
            ref={ref}
            {...props}
          />
        ))}
      />
      {assignDialogOpen && (
        <InventoryBulkPolicyAssignmentDialog
          providerResourceIds={entities.map(
            (entity) => entity.providerResourceId
          )}
          onClose={() => setAssignDialogOpen(false)}
        />
      )}
      {takeSnapshotDialogOpen && (
        <TakeSnapshotDialog
          entities={entities}
          onClose={() => setTakeSnapshotDialogOpen(false)}
        />
      )}
      {dataClassesDiagOpen && (
        <DataClassEditor
          open={true}
          onClose={() => setDataClassesDiagOpen(false)}
          entities={entities}
        />
      )}
      {envDiagOpen && (
        <EnvironmentEditor
          open={true}
          onClose={() => setEnvDiagOpen(false)}
          entities={entities}
        />
      )}
      {appDialogOpen && (
        <AppsEditor
          open={true}
          onClose={() => setAppDialogOpen(false)}
          entities={entities}
        />
      )}
      {resumeAppsDialogOpen && (
        <RestartApplicationDetection
          open={true}
          onClose={() => setResumeAppsDialogOpen(false)}
          entities={entities}
        />
      )}
      {resumeDataClassesDialogOpen && (
        <RestartAutomaticDataClassClassification
          open={true}
          onClose={() => setResumeDataClassesDialogOpen(false)}
          entities={entities}
        />
      )}
      {resumeEnvDialogOpen && (
        <RestartAutomaticEnvironmentDetection
          open={true}
          onClose={() => setResumeEnvDialogOpen(false)}
          entities={entities}
        />
      )}
      <ExcludeFromBackupDialog
        entities={entities}
        isOpen={excludeFromBackupDialogOpen}
        onClose={() => setExcludeFromBackupDialogOpen(false)}
        onRefreshResources={onEntityChange}
      />
      <CancelBackupExclusionDialog
        entities={entities}
        isOpen={cancelBackupExclusionsDialogOpen}
        onClose={() => setCancelBackupExclusionsDialogOpen(false)}
        onRefreshResources={onEntityChange}
      />
      {completeConfigurationDialogOpen &&
        entities.every(
          (e) => e.resourceType === ResourceType.AzureSqlVirtualMachine
        ) && (
          <SqlVmActionRequiredDialog
            resources={entities}
            onClose={() => setCompleteConfigurationDialogOpen(false)}
            onRefreshResources={onEntityChange}
          />
        )}
      {completeConfigurationDialogOpen &&
        entities.every((e) => e.resourceType === ResourceType.GcpBigQuery) && (
          <BigQueryActionRequiredDialog
            resources={entities}
            onClose={() => setCompleteConfigurationDialogOpen(false)}
          />
        )}
    </>
  );
}
